import jwt from "jwt-decode";
import { AXUM_ACCOUNTS_BASE_URL, AXUM_ACCOUNTS_TOKEN } from "../redux/actions/dashboard/env.variables";

interface AxumSetting {
    settingKey: string;
    settingValue: string;
}

export interface TracySyncConfig {
    Enable?: boolean;
    SyncMasterData?: boolean;
    SyncOrders?: boolean;
    UseCheckout?: boolean;
    MasterData?: {
        Articles?: boolean;
        Customers?: boolean;
        Visits?: boolean;
    };
}

class AxumAccountsService {
    readonly loginUrl = AXUM_ACCOUNTS_BASE_URL + '/';

    // Keys de las settings que participan en la decisión de qué grilla mostrar.
    // La setting con prefijo AxAccounts# es la prioritaria; Axia.TracySyncConfig es el fallback.
    // (Ver AxumTracyConnectorCache.GetMergedTracySyncConfig en axum-tracy-middleware).
    private static readonly VERSION_AXIA_KEY = "axia.versionaxia";
    private static readonly TRACY_SYNC_CONFIG_KEY = "axaccounts#axia.tracysyncconfig";
    private static readonly TRACY_SYNC_CONFIG_FALLBACK_KEY = "axia.tracysyncconfig";

    private cachedSettings: AxumSetting[] | null = null;

    /**
     * Trae las settings del usuario actual desde /MySettings (una sola vez) y las cachea.
     */
    private async fetchSettings(token: string): Promise<AxumSetting[] | null> {
        if (this.cachedSettings !== null) {
            return this.cachedSettings;
        }

        try {
            const response = await fetch(`${AXUM_ACCOUNTS_BASE_URL}/MySettings`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                }
            });

            if (!response.ok) {
                console.error("Error fetching settings:", response.statusText);
                return null;
            }

            this.cachedSettings = await response.json();
            return this.cachedSettings;
        } catch (error) {
            console.error("Error calling settings endpoint:", error);
            return null;
        }
    }

    /**
     * Obtiene la setting "Axia.VersionAxia" desde el backend de Axum Accounts.
     */
    async getVersionAxiaSetting(token: string): Promise<string | null> {
        const settings = await this.fetchSettings(token);
        if (!settings) {
            return null;
        }

        const setting = settings.find(
            s => s.settingKey.toLowerCase() === AxumAccountsService.VERSION_AXIA_KEY
        );
        return setting?.settingValue ?? null;
    }

    /**
     * Obtiene el TracySyncConfig mergeado. Prioriza "AxAccounts#Axia.TracySyncConfig" y
     * usa "Axia.TracySyncConfig" como fallback solo si la primaria falta o viene vacía.
     */
    async getTracySyncConfig(token: string): Promise<TracySyncConfig | null> {
        const settings = await this.fetchSettings(token);
        if (!settings) {
            return null;
        }

        const primary = settings.find(
            s => s.settingKey.toLowerCase() === AxumAccountsService.TRACY_SYNC_CONFIG_KEY
        );
        const fallback = settings.find(
            s => s.settingKey.toLowerCase() === AxumAccountsService.TRACY_SYNC_CONFIG_FALLBACK_KEY
        );

        const rawValue =
            primary && primary.settingValue && primary.settingValue.trim() !== ""
                ? primary.settingValue
                : fallback?.settingValue;

        if (!rawValue || rawValue.trim() === "") {
            return null;
        }

        try {
            return JSON.parse(rawValue) as TracySyncConfig;
        } catch (error) {
            console.error("Error parsing TracySyncConfig:", error);
            return null;
        }
    }
}

export default new AxumAccountsService();
